import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import GiftCardPreview from "../components/GiftCardPreview";
import { Link } from "react-router-dom";
import { FEATURE_CORRESPONDANCE } from "../config/features";

const PRODUCTS = [
  { lk: "programme_seul", title: "Programme Essentiel", price: "102€", desc: "52 rendez-vous pendant un an.", cardName: "Programme Essentiel" },
  { lk: "programme_coffret", title: "Programme Épistolaire", price: "152€", desc: "Le programme, le coffret et un envoi manuscrit.", cardName: "Programme Épistolaire" },
  ...(FEATURE_CORRESPONDANCE ? [
    { lk: "correspondance_simple", title: "Réponse simple", price: "35€", desc: "Une lettre manuscrite en retour.", cardName: "Notre petite correspondance — Réponse simple" },
    { lk: "correspondance_coffret", title: "Réponse + coffret", price: "59€", desc: "Une lettre + un petit coffret.", cardName: "Notre petite correspondance — Réponse + coffret" },
  ] : []),
];

export default function GiftCard() {
  const [f, setF] = useState({
    lookup_key: PRODUCTS[0].lk,
    buyer_email: "", buyer_name: "",
    recipient_name: "", dedication: "",
    send_mode: "self",
    recipient_email: "",
    deliver_at: "",  // yyyy-mm-dd from date input
  });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (f.send_mode === "direct" && !f.recipient_email) {
      toast.error("Merci d'indiquer l'email de la destinataire");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...f, origin_url: window.location.origin };
      if (f.send_mode === "direct" && f.deliver_at) {
        // convert yyyy-mm-dd to ISO at 09:00 UTC
        payload.deliver_at = new Date(`${f.deliver_at}T09:00:00Z`).toISOString();
      } else {
        payload.deliver_at = null;
      }
      if (f.send_mode === "self") {
        payload.recipient_email = null;
        payload.deliver_at = null;
      }
      const r = await api.post("/gift-cards/purchase", payload);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setLoading(false);
    }
  }

  return (
    <div data-testid="gift-card-page">
      {/* Hero with live preview */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-16 grid md:grid-cols-12 gap-10 items-center fade-in">
        <div className="md:col-span-6">
          <p className="font-hand text-2xl text-[#A3876A] mb-4 -rotate-2 inline-block">Un cadeau qui prend son temps</p>
          <h1 className="font-serif text-5xl md:text-6xl mb-6">Offrir une <em className="italic">carte cadeau</em></h1>
          <p className="text-lg text-[#4A4036] leading-relaxed max-w-xl mb-6">
            Offre le programme d'écriture{FEATURE_CORRESPONDANCE && " ou une correspondance manuscrite"} à un proche. La carte se compose en direct pendant que tu écris — tu la vois exactement telle qu'elle sera imprimée ou envoyée.
          </p>
          <a href="#composer" className="btn-primary">Composer ma carte <Sparkles size={14}/></a>
        </div>
        <div className="md:col-span-6">
          <div className="rotate-[-1.5deg]">
            <GiftCardPreview
              productName={PRODUCTS.find(p=>p.lk===f.lookup_key)?.cardName}
              recipientName={f.recipient_name}
              buyerName={f.buyer_name}
              dedication={f.dedication}
            />
          </div>
        </div>
      </section>
      {/* 1 · Cartes cadeaux du Club de Lettres */}
      <section data-testid="gift-club-section" className="bg-[#F5F0E6] py-14 border-y border-[#E8DCC8]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <p className="font-hand text-xl text-[#A3876A] mb-2">Le cadeau qui dure</p>
            <h2 className="font-serif text-2xl md:text-3xl mb-3">Offrir le Club de Lettres — 3, 6 ou 12 mois</h2>
            <p className="text-[#4A4036] leading-relaxed mb-4">Chaque mois, une enveloppe remplie de douceurs et de petites attentions arrive dans la boîte aux lettres d'un proche. Réglé en une fois, sans renouvellement automatique. Première enveloppe le mois suivant l'achat, expédiée autour du 10.</p>
            <div className="grid grid-cols-2 gap-3 text-sm mb-5">
              <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-3"><p className="text-xs uppercase tracking-wider text-[#7C7064] mb-1">France métropolitaine</p><p>3 mois <strong>35 €</strong> · 6 mois <strong>70 €</strong> · 12 mois <strong>140 €</strong></p></div>
              <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-3"><p className="text-xs uppercase tracking-wider text-[#7C7064] mb-1">International</p><p>3 mois <strong>45 €</strong> · 6 mois <strong>90 €</strong> · 12 mois <strong>180 €</strong></p></div>
            </div>
            <Link data-testid="cta-offrir-mailclub" to="/club-de-lettres/offrir" className="btn-primary inline-flex items-center gap-2"><Sparkles size={16}/> Offrir le Club de Lettres</Link>
          </div>
          <div className="md:col-span-5"><img src="/club-enveloppe-douce.jpg" alt="Une enveloppe du Club de Lettres, nouée d'un ruban doux et scellée à la cire" className="w-full h-[320px] object-cover border border-[#E8DCC8]"/></div>
        </div>
      </section>


      {/* How it works */}
      <section className="bg-[#F5F0E6] py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-4xl text-center mb-16">Comment ça marche</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Tu composes", d: "Tu choisis la formule et écris un mot doux. La carte se dessine en direct." },
              { n: "02", t: "Tu choisis le mode", d: "Imprimer toi-même pour glisser dans une enveloppe, ou envoyer par email au jour de ton choix." },
              { n: "03", t: "Il ou elle en profite", d: "La personne saisit le code sur le site pour recevoir son cadeau." },
            ].map((s, i) => (
              <div key={i} className="text-left">
                <p className="font-hand text-3xl text-[#A3876A] mb-3">{s.n}</p>
                <h3 className="font-serif text-2xl mb-3">{s.t}</h3>
                <p className="text-[#4A4036] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Composer — form + sticky preview */}
      <section id="composer" className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <h2 className="font-serif text-4xl mb-3">Compose ta carte cadeau</h2>
          <p className="text-[#7C7064] mb-10">Paiement sécurisé Stripe · code valable 12 mois</p>

          <form onSubmit={submit} className="space-y-10">
            <div>
              <p className="font-hand text-xl text-[#A3876A] mb-4">Quelle formule ?</p>
              <div className="grid md:grid-cols-2 gap-4">
                {PRODUCTS.map(p => (
                  <label key={p.lk} data-testid={`gift-product-${p.lk}`}
                    className={`p-6 border cursor-pointer transition-colors ${f.lookup_key === p.lk ? "border-[#A3876A] bg-[#FDFBF7]" : "border-[#E8DCC8] bg-[#F5F0E6] hover:bg-[#FDFBF7]"}`}>
                    <input type="radio" name="lk" value={p.lk} checked={f.lookup_key === p.lk} onChange={()=>setF({...f, lookup_key: p.lk})} className="sr-only"/>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-serif text-xl">{p.title}</p>
                        <p className="text-sm text-[#7C7064] mt-1">{p.desc}</p>
                      </div>
                      <p className="font-serif text-2xl text-[#2C2621]">{p.price}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="font-hand text-xl text-[#A3876A] mb-4">Tes coordonnées</p>
              <div className="grid md:grid-cols-2 gap-6">
                <input data-testid="gift-buyer-name" className="field-input" placeholder="Ton prénom" required value={f.buyer_name} onChange={e=>setF({...f, buyer_name: e.target.value})}/>
                <input data-testid="gift-buyer-email" className="field-input" type="email" placeholder="Ton email" required value={f.buyer_email} onChange={e=>setF({...f, buyer_email: e.target.value})}/>
              </div>
            </div>

            <div>
              <p className="font-hand text-xl text-[#A3876A] mb-4">À qui offres-tu ?</p>
              <div className="space-y-6">
                <input data-testid="gift-recipient-name" className="field-input" placeholder="Prénom de la personne à qui tu offres" required value={f.recipient_name} onChange={e=>setF({...f, recipient_name: e.target.value})}/>
                <textarea data-testid="gift-dedication" className="field-input" placeholder="Un mot doux à écrire sur la carte (facultatif)" rows={3} maxLength={240} value={f.dedication} onChange={e=>setF({...f, dedication: e.target.value})}/>
                <p className="text-xs text-[#7C7064] text-right">{f.dedication.length}/240</p>
              </div>
            </div>

            <div>
              <p className="font-hand text-xl text-[#A3876A] mb-4">Comment souhaites-tu l'offrir ?</p>
              <div className="grid md:grid-cols-2 gap-4">
                <label data-testid="mode-self" className={`p-6 border cursor-pointer ${f.send_mode === "self" ? "border-[#A3876A] bg-[#FDFBF7]" : "border-[#E8DCC8] bg-[#F5F0E6]"}`}>
                  <input type="radio" name="mode" checked={f.send_mode==="self"} onChange={()=>setF({...f, send_mode:"self"})} className="sr-only"/>
                  <p className="font-serif text-xl">Je l'imprime moi-même</p>
                  <p className="text-sm text-[#7C7064] mt-1">Tu reçois la carte par email et tu l'imprimes pour l'offrir en main propre ou par courrier.</p>
                </label>
                <label data-testid="mode-direct" className={`p-6 border cursor-pointer ${f.send_mode === "direct" ? "border-[#A3876A] bg-[#FDFBF7]" : "border-[#E8DCC8] bg-[#F5F0E6]"}`}>
                  <input type="radio" name="mode" checked={f.send_mode==="direct"} onChange={()=>setF({...f, send_mode:"direct"})} className="sr-only"/>
                  <p className="font-serif text-xl">Envoi direct par email</p>
                  <p className="text-sm text-[#7C7064] mt-1">La destinataire reçoit un joli email — le jour de ton choix.</p>
                </label>
              </div>
              {f.send_mode === "direct" && (
                <div className="mt-6 grid md:grid-cols-2 gap-6 bg-[#F5F0E6] p-6 border border-[#E8DCC8]">
                  <div>
                    <label className="text-sm text-[#7C7064] block mb-2">Email de la destinataire</label>
                    <input data-testid="gift-recipient-email" className="field-input" type="email" required placeholder="prenom@exemple.fr" value={f.recipient_email} onChange={e=>setF({...f, recipient_email: e.target.value})}/>
                  </div>
                  <div>
                    <label className="text-sm text-[#7C7064] block mb-2">Date d'envoi (facultatif — vide = tout de suite)</label>
                    <input data-testid="gift-deliver-at" className="field-input" type="date" min={new Date().toISOString().slice(0,10)} value={f.deliver_at} onChange={e=>setF({...f, deliver_at: e.target.value})}/>
                  </div>
                  <p className="text-xs text-[#7C7064] md:col-span-2">L'email sera envoyé le matin à 9h (heure de Paris). Tu recevras une confirmation.</p>
                </div>
              )}
            </div>

            <button data-testid="gift-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center">
              {loading ? "Redirection…" : <>Composer ma carte <Sparkles size={14}/></>}
            </button>
            <p className="text-xs text-[#7C7064] text-center">Après paiement, tu recevras la carte cadeau par email avec un lien pour l'imprimer.</p>
          </form>
        </div>

        {/* Sticky live preview */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <p className="font-hand text-xl text-[#A3876A] mb-4 text-center">Aperçu en direct</p>
            <GiftCardPreview
              productName={PRODUCTS.find(p=>p.lk===f.lookup_key)?.cardName}
              recipientName={f.recipient_name}
              buyerName={f.buyer_name}
              dedication={f.dedication}
              scale="lg"
            />
            <p className="text-xs text-[#7C7064] mt-4 text-center">
              La carte se met à jour pendant que tu écris. Le code sera généré après paiement.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
